// RouteErrorBoundary — wraps the routed content so a single page error never
// takes the whole app down. The global header + bottom tab bar live OUTSIDE this
// boundary, so when a page throws the chrome stays put and the user can just tap
// another tab (which changes the route key and resets this boundary) or hit
// "Try again". Last-resort full-app crashes are handled by the top-level
// Sentry.ErrorBoundary in main.tsx.
import * as Sentry from "@sentry/react";
import { Link } from "react-router-dom";
import { AlertTriangle, RotateCw } from "lucide-react";

function RouteErrorFallback({ resetError }: { resetError: () => void }) {
  return (
    <div
      style={{
        minHeight: "55vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "72px 24px",
        textAlign: "center",
        fontFamily: "'Instrument Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      <div style={{ maxWidth: 400 }}>
        <AlertTriangle style={{ width: 40, height: 40, color: "var(--civic-red)", margin: "0 auto 16px" }} />
        <h1 style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontSize: 28, fontWeight: 400, color: "var(--civic-navy)", marginBottom: 10 }}>
          This page hit a snag
        </h1>
        <p style={{ fontSize: 15, lineHeight: 1.5, color: "rgba(var(--civic-ink-rgb),0.6)", marginBottom: 22 }}>
          Something went wrong loading this page — but the app is fine. Try again, or use the tabs below to go somewhere else.
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <button
            onClick={resetError}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 20px", borderRadius: 10, border: "none", cursor: "pointer", background: "var(--civic-red)", color: "#fff", fontSize: 15, fontWeight: 600, fontFamily: "inherit" }}
          >
            <RotateCw style={{ width: 16, height: 16 }} /> Try again
          </button>
          <Link
            to="/near-me"
            onClick={resetError}
            style={{ display: "inline-flex", alignItems: "center", padding: "11px 20px", borderRadius: 10, textDecoration: "none", border: "1px solid rgba(var(--civic-navy-rgb),0.2)", color: "var(--civic-navy)", fontSize: 15, fontWeight: 600 }}
          >
            Go to Near Me
          </Link>
        </div>
      </div>
    </div>
  );
}

export function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <RouteErrorFallback resetError={resetError} />}>
      {children}
    </Sentry.ErrorBoundary>
  );
}
